<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import disconnectIcon from '@/assets/icons/disconnect.svg';
import dotsIcon from '@/assets/icons/dots.svg';
import eyeIcon from '@/assets/icons/eye.svg';
import pencilIcon from '@/assets/icons/pencil.svg';
import { placeholderBackgroundColor } from '@/helpers/colors';
import { getInitials } from '@/helpers/formatters';
import { useI18n } from '@/references/i18n';
import { RouteNames } from '@/router';

import type { BeneficiarySelectorItemInfo } from '@/components/BeneficiarySelectorItem.types';
import UiButtonIcon from '@/components/ui/UiButtonIcon.vue';
import UiColoredCircle from '@/components/ui/UiColoredCircle.vue';
import type { UiDropdownItem } from '@/components/ui/UiDropdown.types';
import UiDropdown from '@/components/ui/UiDropdown.vue';
import UiText from '@/components/ui/UiText.vue';

interface Props {
  info: BeneficiarySelectorItemInfo;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'select'): void;
  (event: 'remove'): void;
}>();

const { t } = useI18n();
const { push } = useRouter();

const options = computed((): UiDropdownItem[] => [
  {
    text: t('home.beneficiaries.contextMenu.details'),
    icon: eyeIcon,
    handler() {
      push({
        name: RouteNames.Beneficiary,
        params: { id: props.info.id }
      });
    }
  },
  {
    text: t('home.beneficiaries.contextMenu.customize'),
    icon: pencilIcon,
    handler() {
      push({
        name: RouteNames.EditBeneficiary,
        params: { id: props.info.id }
      });
    }
  },
  {
    text: t('home.beneficiaries.contextMenu.remove'),
    icon: disconnectIcon,
    red: true,
    handler() {
      emit('remove');
    }
  }
]);

function onClick(): void {
  emit('select');
}
</script>

<template>
  <div class="beneficiary-selector-item">
    <button class="beneficiary-selector-item__button" type="button" @click="onClick">
      <UiColoredCircle
        class="beneficiary-selector-item__icon"
        :text="getInitials(props.info.title)"
        :color="placeholderBackgroundColor(props.info.title)"
      />
      <span class="beneficiary-selector-item__item-info">
        <UiText class="beneficiary-selector-item__title" weight="semibold" size="md">
          {{ props.info.title }}
        </UiText>
        <UiText class="beneficiary-selector-item__description" size="sm" muted>
          {{ t('home.beneficiaries.recent.description', { count: props.info.amount }) }}
        </UiText>
      </span>
    </button>
    <UiDropdown
      class="beneficiary-selector-item__dropdown"
      position="bottom-end"
      :items="options"
      muted-icons
      :margin="8"
    >
      <UiButtonIcon :icon="dotsIcon" mod="transparent-muted" />
    </UiDropdown>
  </div>
</template>

<style scoped>
.beneficiary-selector-item {
  position: relative;
}

.beneficiary-selector-item__dropdown {
  position: absolute;
  top: calc(50% - 20px);
  right: -8px;
}

.beneficiary-selector-item__button {
  display: flex;
  align-items: center;
  width: calc(100% + 16px);
  margin: -8px;
  padding: 8px 48px 8px 8px;
  color: inherit;
  font-weight: inherit;
  font-size: inherit;
  font-family: inherit;
  line-height: inherit;
  text-align: left;
  word-break: break-word;
  background-color: transparent;
  border: none;
  border-radius: 0;
  cursor: pointer;
  appearance: none;
  user-select: none;
}

.beneficiary-selector-item__icon {
  flex-shrink: 0;
  margin: 0 16px 0 0;
}

.beneficiary-selector-item__title,
.beneficiary-selector-item__description {
  display: block;
}
</style>
